import { DataModel } from "../types/gantt-types";

export const formatData = (data: DataModel[]) => {
  const hasFact = data.some(d => d.spanType === 'fact');
  const isAlreadyTree = data.some(d => d.children && d.children.length > 0);

  if (hasFact || isAlreadyTree) {
    if (hasFact) {
      // Grouped mode
      data.forEach(item => {
        if (item.spanType === 'fact' && item.children) {
          item.children.forEach(trace => {
            const traceItems: DataModel[] = [];
            const collect = (n: DataModel) => {
              traceItems.push(n);
              if (n.children) n.children.forEach(collect);
            };
            collect(trace);
            if (traceItems.length > 0) {
              updateStartTime(traceItems);
            }
          });
          item.startms = 0;
        }
      });
    } else {
      // Single trace mode
      const allItems: DataModel[] = [];
      const collectItems = (items: DataModel[]) => {
        items.forEach(item => {
          allItems.push(item);
          if (item.children && item.children.length > 0) {
            collectItems(item.children);
          }
        });
      };
      collectItems(data);

      if (allItems.length > 0) {
        updateStartTime(allItems);
      }
    }

    return insertRowNumber(data);
  }

  let res: DataModel[] = [];
  const children: DataModel[] = [];
  const updatedData = updateStartTime(data);
  updatedData.forEach((d) => {
    if (d.parentId) {
      children.push(d);
    }

    res.push(d);
  });

  res = list_to_tree(res);
  res = insertRowNumber(res);
  return updateStartTime(res);
};

// we insert indexes to each row, to maintain the expand/collapse state in chart, when a row is collapsed, all its children are hidden and next row has its own index, not the next number.

const insertRowNumber = (
  data: DataModel[],
  indexRef = { value: 1 }
): DataModel[] => {
  data.forEach((d) => {
    d.index = indexRef.value++;
    if (d.children) {
      insertRowNumber(d.children, indexRef);
    }
  });
  return data;
};

const list_to_tree = (list: DataModel[]) => {
  const map: { [key: string]: number } = {};
  let node: DataModel;
  const roots: DataModel[] = [];
  for (let i = 0; i < list.length; i += 1) {
    map[list[i].id] = i; // initialize the map
    list[i].children = []; // initialize the children
  }
  for (let i = 0; i < list.length; i += 1) {
    node = list[i];
    if (node.parentId) {
      // if you have dangling branches check that map[node.parentId] exists
      const mapNode = map[node.parentId];
      if (mapNode !== undefined) {
        const listNode = list[mapNode];
        if (listNode) {
          listNode.children?.push(node);
        }
      }
    } else {
      roots.push(node);
    }
  }
  return roots;
};
const updateStartTime = (data: DataModel[]) => {
  const lowestData = data.sort((a, b) =>
    a?.startTime < b?.startTime ? -1 : 1
  )[0];
  const lowestStartDate = new Date(lowestData?.startTime);

  data.forEach((d) => {
    d.startms = new Date(d.startTime).getTime() - lowestStartDate.getTime();
  });
  return data;
};

export const findMaxDuration = (data: DataModel[]) => {
  if (data && data.length) {
    let maxDur = 0;
        // Recursively find the longest duration in the tree
    const findMax = (items: DataModel[]) => {
      items.forEach(item => {
        if (item.duration > maxDur) maxDur = item.duration;
        if (item.children && item.children.length > 0) {
          findMax(item.children);
        }
      });
    };
    findMax(data);
    return maxDur || 100;
  }
  return 100;
};

const DURATION_UNITS = [
  { divisor: 1, unitLabel: 'ms' },
  { divisor: 1000, unitLabel: 's' },
  { divisor: 60_000, unitLabel: 'min' },
  { divisor: 3_600_000, unitLabel: 'hr' },
];

// Largest value we're willing to display in a given unit before escalating
// to the next one (e.g. switch from seconds to minutes once seconds would
// exceed 3 digits).
const MAX_UNIT_VALUE = 999;

// Picks a single display unit (ms/s/min/hr) for a chart based on its max duration,
// so every axis tick and end-of-chart label reads consistently. Picks the
// smallest unit whose displayed value still fits within MAX_UNIT_VALUE, so
// e.g. a ~452s range stays in seconds (0s,50s,...,450s) instead of jumping to
// minutes just because 60s has elapsed.
export const getDurationUnit = (maxMs: number): { divisor: number; unitLabel: string } => {
  const unit = DURATION_UNITS.find((u) => maxMs / u.divisor <= MAX_UNIT_VALUE);
  return unit ?? DURATION_UNITS[DURATION_UNITS.length - 1];
};
